import { Link, Navigate, useParams } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { CTABand } from "../components/CTABand";
import { FadeUp } from "../components/Reveal";
import { useContent } from "../context/ContentContext";

export default function ServicePage() {
  const { slug } = useParams();
  const { services } = useContent();
  const service = services.find((s) => s.slug === slug);

  if (!service) return <Navigate to="/" replace />;

  const index = services.findIndex((s) => s.slug === slug);
  const next = services[(index + 1) % services.length];

  return (
    <main data-testid={`service-page-${service.slug}`}>
      <PageHero
        eyebrow={`Diensten: ${service.title}`}
        title={service.title}
        intro={service.intro}
        image={service.image}
      />

      <section className="bg-white">
        <div className="mx-auto grid max-w-[1400px] gap-10 px-4 py-12 sm:px-6 lg:grid-cols-3">
          <div>
            <FadeUp>
              <h2 className="font-display text-2xl font-bold uppercase tracking-wide text-[#111111]">
                Werkzaamheden
              </h2>
              <p className="mt-3 text-[15px] leading-relaxed text-[#555555]">
                Een overzicht van wat ik voor je kan doen op het gebied van{" "}
                {service.title.toLowerCase()}.
              </p>
            </FadeUp>
          </div>
          <div className="lg:col-span-2">
            <ul>
              {service.points.map((point, i) => (
                <FadeUp key={point} delay={i * 0.05}>
                  <li
                    data-testid={`service-point-${i}`}
                    className="flex items-center gap-3 border-b border-[#E2E0D9] py-4 text-[15px] text-[#333333]"
                  >
                    <ChevronRight className="h-4 w-4 shrink-0 text-[#8E8D8A]" />
                    {point}
                  </li>
                </FadeUp>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="bg-[#EFEDE8]">
        <div className="mx-auto max-w-[1400px] px-4 py-12 sm:px-6">
          <div className="grid gap-10 sm:grid-cols-2">
            {service.sections.map((sec, i) => (
              <FadeUp key={sec.heading} delay={i * 0.08}>
                <div data-testid={`service-section-${i}`}>
                  <h3 className="font-display text-xl font-bold uppercase tracking-wide text-[#111111]">
                    {sec.heading}
                  </h3>
                  <p className="mt-3 text-[15px] leading-relaxed text-[#555555]">
                    {sec.text}
                  </p>
                </div>
              </FadeUp>
            ))}
          </div>

          <FadeUp delay={0.15}>
            <div className="mt-12 flex flex-wrap items-center justify-between gap-4 border-t border-[#DDD9D0] pt-6">
              <Link
                to="/contact"
                data-testid="service-page-offerte-link"
                className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wide text-[#111111] hover:underline"
              >
                Direct een offerte aanvragen
                <ChevronRight className="h-4 w-4" />
              </Link>
              <Link
                to={`/diensten/${next.slug}`}
                data-testid="service-page-next-link"
                className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wide text-[#8E8D8A] transition-colors duration-200 hover:text-[#111111]"
              >
                Volgende dienst: {next.title}
                <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>

      {service.faq && (
        <section data-testid="service-faq" className="bg-white">
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: service.faq.map((item) => ({
                "@type": "Question",
                name: item.q,
                acceptedAnswer: { "@type": "Answer", text: item.a },
              })),
            })}
          </script>
          <div className="mx-auto grid max-w-[1400px] gap-10 px-4 py-12 sm:px-6 lg:grid-cols-3">
            <FadeUp>
              <h2 className="font-display text-2xl font-bold uppercase tracking-wide text-[#111111]">
                Veelgestelde vragen
              </h2>
              <p className="mt-3 text-[15px] leading-relaxed text-[#555555]">
                Staat je vraag er niet bij? Bel of mail gerust, ik denk graag mee.
              </p>
            </FadeUp>
            <div className="lg:col-span-2">
              {service.faq.map((item, i) => (
                <FadeUp key={item.q} delay={i * 0.05}>
                  <div data-testid={`service-faq-${i}`} className="border-b border-[#E2E0D9] py-5">
                    <h3 className="font-display text-lg font-bold uppercase tracking-wide text-[#111111]">
                      {item.q}
                    </h3>
                    <p className="mt-2 text-[15px] leading-relaxed text-[#555555]">{item.a}</p>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </section>
      )}

      <CTABand
        title={`${service.title} nodig in Opmeer of omgeving?`}
        text="Vertel kort over je project. Ik kom vrijblijvend kijken, meet in en maak een heldere offerte."
      />
    </main>
  );
}
